/**
 * CHASE Lead Recovery & Reactivation Engine — Management Dashboard
 * Stage 2 · Preview Certification Build
 *
 * Engine remains DISABLED until Management separately authorizes the Production pilot.
 * All controls are advisory / configuration only.
 */

import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Zap, Users, Mail, Pause, Play, Ban, Eye, BarChart2,
  CheckCircle, XCircle, AlertTriangle, Settings, ChevronRight,
  RefreshCw, Inbox, Clock, TrendingUp, Shield
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

const authHeader = () => {
  const t = localStorage.getItem("token");
  return t ? { Authorization: `Bearer ${t}` } : {};
};

const ax = (method, url, data) =>
  axios({ method, url: `${API}${url}`, data, headers: authHeader() });

// ── State badge ────────────────────────────────────────────────────────────────
const STATE_META = {
  inactive:    { label: "Inactive",     color: "bg-slate-100 text-slate-600",   icon: Clock },
  scheduled:   { label: "Scheduled",    color: "bg-sky-100 text-sky-700",        icon: Clock },
  in_sequence: { label: "In Sequence",  color: "bg-emerald-100 text-emerald-700",icon: TrendingUp },
  paused:      { label: "Paused",       color: "bg-amber-100 text-amber-700",    icon: Pause },
  suppressed:  { label: "Suppressed",   color: "bg-rose-100 text-rose-700",      icon: Ban },
  stopped:     { label: "Stopped",      color: "bg-slate-200 text-slate-700",    icon: CheckCircle },
  no_qualifying_comm: { label: "Needs First Contact", color: "bg-purple-100 text-purple-700", icon: Mail },
  opted_out:   { label: "Opted Out",    color: "bg-rose-50 text-rose-500",       icon: Ban },
};

function StateBadge({ status }) {
  const m = STATE_META[status] || { label: status, color: "bg-slate-100 text-slate-500" };
  const Icon = m.icon || null;
  return (
    <span className={`inline-flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded-full ${m.color}`}>
      {Icon && <Icon size={10} />}
      {m.label}
    </span>
  );
}

// ── Stat card ─────────────────────────────────────────────────────────────────
function StatCard({ label, value, icon: Icon, color = "text-slate-700", sub }) {
  return (
    <div className="bg-white rounded-xl border border-slate-200 p-4 shadow-sm">
      <div className="flex items-start justify-between">
        <div>
          <p className="text-xs text-slate-500 font-medium uppercase tracking-wide">{label}</p>
          <p className={`text-2xl font-bold mt-1 ${color}`}>{value ?? "—"}</p>
          {sub && <p className="text-xs text-slate-400 mt-0.5">{sub}</p>}
        </div>
        {Icon && <Icon size={20} className="text-slate-300 mt-0.5" />}
      </div>
    </div>
  );
}

// ── Main component ─────────────────────────────────────────────────────────────
export default function LeadRecoveryDashboard() {
  const [tab, setTab] = useState("overview");
  const [stats, setStats] = useState(null);
  const [leads, setLeads] = useState([]);
  const [leadsTotal, setLeadsTotal] = useState(0);
  const [leadsStatus, setLeadsStatus] = useState("");
  const [messages, setMessages] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [simResult, setSimResult] = useState(null);
  const [simId, setSimId] = useState("");
  const [simRunning, setSimRunning] = useState(false);
  const [dryRunRunning, setDryRunRunning] = useState(false);
  const [dryRunResult, setDryRunResult] = useState(null);

  const loadStats = useCallback(async () => {
    try {
      const r = await ax("get", "/api/lead-recovery/stats");
      setStats(r.data);
    } catch { /* handled globally */ }
  }, []);

  const loadLeads = useCallback(async (status = "") => {
    try {
      const qs = status ? `?status=${status}&limit=50` : "?limit=50";
      const r = await ax("get", `/api/lead-recovery/leads${qs}`);
      setLeads(r.data.items || []);
      setLeadsTotal(r.data.total || 0);
    } catch { /* handled globally */ }
  }, []);

  const loadMessages = useCallback(async () => {
    try {
      const r = await ax("get", "/api/lead-recovery/messages?limit=30");
      setMessages(r.data.items || []);
    } catch { /* handled globally */ }
  }, []);

  const loadTemplates = useCallback(async () => {
    try {
      const r = await ax("get", "/api/lead-recovery/templates");
      setTemplates(r.data.templates || []);
    } catch { /* handled globally */ }
  }, []);

  const loadConfig = useCallback(async () => {
    try {
      const r = await ax("get", "/api/lead-recovery/config");
      setConfig(r.data);
    } catch { /* handled globally */ }
  }, []);

  const refreshAll = useCallback(async () => {
    setLoading(true);
    await Promise.all([loadStats(), loadLeads(leadsStatus), loadMessages(), loadTemplates(), loadConfig()]);
    setLoading(false);
  }, [loadStats, loadLeads, loadMessages, loadTemplates, loadConfig, leadsStatus]);

  useEffect(() => { refreshAll(); }, []);

  const toggleEngine = async () => {
    if (!config) return;
    const newEnabled = !config.enabled;
    if (newEnabled) {
      const ok = window.confirm(
        "Enable the recovery engine?\n\nIMPORTANT: Production marketing emails remain disabled until Management separately authorizes the Production pilot. The engine will only send emails if fully authorized and within the configured sending window."
      );
      if (!ok) return;
    }
    try {
      const r = await ax("put", "/api/lead-recovery/config", { enabled: newEnabled });
      setConfig(r.data);
      toast.success(`Recovery engine ${newEnabled ? "enabled" : "disabled"}`);
    } catch (e) {
      toast.error("Config update failed: " + (e.response?.data?.detail || e.message));
    }
  };

  const updateConfig = async (key, value) => {
    try {
      const r = await ax("put", "/api/lead-recovery/config", { [key]: value });
      setConfig(r.data);
      toast.success("Config updated");
    } catch (e) {
      toast.error("Config update failed: " + (e.response?.data?.detail || e.message));
    }
  };

  const leadAction = async (clientId, action, reason = "") => {
    try {
      await ax("patch", `/api/lead-recovery/leads/${clientId}/${action}`, reason ? { reason } : {});
      toast.success(`Lead ${action}d`);
      await loadLeads(leadsStatus);
      await loadStats();
    } catch (e) {
      toast.error(`Action failed: ${e.response?.data?.detail || e.message}`);
    }
  };

  const simulate = async () => {
    if (!simId.trim()) return;
    setSimRunning(true);
    setSimResult(null);
    try {
      const r = await ax("post", `/api/lead-recovery/simulate/${simId.trim()}`);
      setSimResult(r.data);
    } catch (e) {
      setSimResult({ error: e.response?.data?.detail || e.message });
    } finally {
      setSimRunning(false);
    }
  };

  const dryRun = async () => {
    setDryRunRunning(true);
    setDryRunResult(null);
    try {
      const r = await ax("post", "/api/lead-recovery/dispatch/run?dry_run=true");
      setDryRunResult(r.data);
      toast.success(`Dry run complete: ${r.data.sent || 0} would send`);
    } catch (e) {
      toast.error("Dry run failed: " + (e.response?.data?.detail || e.message));
    } finally {
      setDryRunRunning(false);
    }
  };

  if (loading && !stats) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="animate-spin text-slate-400" size={28} />
      </div>
    );
  }

  const imap_ok = stats?.imap_healthy;
  const engine_on = stats?.engine_enabled;

  return (
    <div className="min-h-screen bg-slate-50 p-6" data-testid="lead-recovery-dashboard">
      {/* Header */}
      <div className="flex items-start justify-between mb-6">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <Zap size={20} className="text-purple-600" />
            <h1 className="text-xl font-bold text-slate-900">CHASE Lead Recovery</h1>
            <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${engine_on ? "bg-emerald-100 text-emerald-700" : "bg-slate-200 text-slate-500"}`}>
              {engine_on ? "Engine On" : "Engine Off"}
            </span>
            {!imap_ok && (
              <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-amber-100 text-amber-700 flex items-center gap-1">
                <AlertTriangle size={10} /> IMAP Degraded
              </span>
            )}
          </div>
          <p className="text-sm text-slate-500">
            Authorized cadence: Day 7 · 15 · 30 · 45 · then every 90 days &mdash; Leads only
          </p>
        </div>
        <button
          data-testid="lr-refresh-btn"
          onClick={refreshAll}
          className="flex items-center gap-1.5 px-3 py-1.5 text-sm text-slate-600 bg-white border border-slate-200 rounded-lg hover:bg-slate-50 transition"
        >
          <RefreshCw size={14} className={loading ? "animate-spin" : ""} />
          Refresh
        </button>
      </div>

      {/* Warning banner if engine off or IMAP degraded */}
      {(!engine_on || !imap_ok) && (
        <div className={`mb-5 px-4 py-3 rounded-lg text-sm flex items-start gap-2 ${!engine_on ? "bg-amber-50 border border-amber-200 text-amber-800" : "bg-rose-50 border border-rose-200 text-rose-800"}`}
          data-testid="lr-warning-banner">
          <AlertTriangle size={16} className="mt-0.5 shrink-0" />
          <span>
            {!engine_on && "Recovery engine is currently DISABLED. No recovery emails will be sent. Enable via the Config tab once Management authorizes the Production pilot. "}
            {!imap_ok && "CHASE IMAP is not healthy. Outbound recovery is blocked until IMAP sync is confirmed operational."}
          </span>
        </div>
      )}

      {/* Nav tabs */}
      <div className="flex gap-1 mb-5 border-b border-slate-200" data-testid="lr-tabs">
        {[
          { id: "overview", label: "Overview", icon: BarChart2 },
          { id: "leads", label: "Leads", icon: Users },
          { id: "messages", label: "Message Log", icon: Mail },
          { id: "templates", label: "Templates", icon: Inbox },
          { id: "config", label: "Config", icon: Settings },
          { id: "simulate", label: "Simulate", icon: Zap },
        ].map(({ id, label, icon: Icon }) => (
          <button
            key={id}
            data-testid={`lr-tab-${id}`}
            onClick={() => setTab(id)}
            className={`flex items-center gap-1.5 px-4 py-2 text-sm font-medium border-b-2 transition -mb-px ${
              tab === id
                ? "border-purple-600 text-purple-700"
                : "border-transparent text-slate-500 hover:text-slate-700"
            }`}
          >
            <Icon size={14} />
            {label}
          </button>
        ))}
      </div>

      {/* ── Overview ── */}
      {tab === "overview" && stats && (
        <div data-testid="lr-overview-panel">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
            <StatCard label="Total Leads" value={stats.total_leads} icon={Users} />
            <StatCard label="In Sequence" value={stats.state_counts?.in_sequence ?? 0} icon={TrendingUp} color="text-emerald-700" />
            <StatCard label="Pending Replies" value={stats.pending_replies} icon={Inbox} color="text-amber-700" />
            <StatCard label="Sent (30d)" value={stats.messages_sent_30d} icon={Mail} sub={`${stats.messages_sent_total} total`} />
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
            {Object.entries(stats.state_counts || {}).map(([k, v]) => (
              <div key={k} className="bg-white rounded-xl border border-slate-100 p-3 shadow-sm">
                <StateBadge status={k} />
                <p className="text-xl font-bold text-slate-800 mt-2">{v}</p>
              </div>
            ))}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="bg-white rounded-xl border border-slate-200 p-4 shadow-sm">
              <h3 className="text-sm font-semibold text-slate-700 mb-3 flex items-center gap-1.5">
                <Shield size={14} /> Engine Status
              </h3>
              <dl className="space-y-1.5">
                <div className="flex justify-between text-sm">
                  <dt className="text-slate-500">Engine</dt>
                  <dd className={`font-medium ${engine_on ? "text-emerald-600" : "text-slate-500"}`}>
                    {engine_on ? "Enabled" : "Disabled"}
                  </dd>
                </div>
                <div className="flex justify-between text-sm">
                  <dt className="text-slate-500">CHASE IMAP</dt>
                  <dd className={`font-medium ${imap_ok ? "text-emerald-600" : "text-amber-600"}`}>
                    {stats.imap_health || "unknown"} {imap_ok ? "" : "(outbound blocked)"}
                  </dd>
                </div>
                <div className="flex justify-between text-sm">
                  <dt className="text-slate-500">Daily Cap</dt>
                  <dd className="font-medium text-slate-700">{stats.daily_cap} emails/day</dd>
                </div>
                <div className="flex justify-between text-sm">
                  <dt className="text-slate-500">Sending Window</dt>
                  <dd className="font-medium text-slate-700">{stats.sending_window}</dd>
                </div>
                <div className="flex justify-between text-sm">
                  <dt className="text-slate-500">Pilot Mode</dt>
                  <dd className={`font-medium ${stats.pilot_mode ? "text-amber-600" : "text-slate-500"}`}>
                    {stats.pilot_mode ? "Active" : "Off"}
                  </dd>
                </div>
                <div className="flex justify-between text-sm">
                  <dt className="text-slate-500">Failed Messages</dt>
                  <dd className={`font-medium ${stats.messages_failed > 0 ? "text-rose-600" : "text-slate-500"}`}>
                    {stats.messages_failed}
                  </dd>
                </div>
              </dl>
            </div>

            <div className="bg-white rounded-xl border border-slate-200 p-4 shadow-sm">
              <h3 className="text-sm font-semibold text-slate-700 mb-3 flex items-center gap-1.5">
                <Zap size={14} className="text-purple-600" /> Quick Actions
              </h3>
              <div className="space-y-2">
                <button
                  data-testid="lr-dry-run-btn"
                  onClick={dryRun}
                  disabled={dryRunRunning}
                  className="w-full flex items-center justify-between px-4 py-2.5 rounded-lg border border-purple-200 bg-purple-50 hover:bg-purple-100 text-purple-700 text-sm font-medium transition disabled:opacity-50"
                >
                  <span className="flex items-center gap-1.5">
                    <Zap size={14} /> Run Dry-Run (no emails sent)
                  </span>
                  <ChevronRight size={14} />
                </button>
                <button
                  data-testid="lr-toggle-engine-btn"
                  onClick={toggleEngine}
                  className={`w-full flex items-center justify-between px-4 py-2.5 rounded-lg border text-sm font-medium transition ${
                    engine_on
                      ? "border-rose-200 bg-rose-50 hover:bg-rose-100 text-rose-700"
                      : "border-emerald-200 bg-emerald-50 hover:bg-emerald-100 text-emerald-700"
                  }`}
                >
                  <span className="flex items-center gap-1.5">
                    {engine_on ? <Pause size={14} /> : <Play size={14} />}
                    {engine_on ? "Disable Engine" : "Enable Engine"}
                  </span>
                  <ChevronRight size={14} />
                </button>
              </div>
              {dryRunResult && (
                <div className="mt-3 p-3 bg-slate-50 rounded-lg text-xs text-slate-700 border border-slate-100" data-testid="lr-dry-run-result">
                  <p className="font-semibold mb-1">Dry Run Result</p>
                  <p>Would send: <strong>{dryRunResult.sent}</strong></p>
                  <p>Skipped: {dryRunResult.skipped} · Errors: {dryRunResult.errors}</p>
                  <p className="text-slate-400">{dryRunResult.detail || dryRunResult.status}</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ── Leads ── */}
      {tab === "leads" && (
        <div data-testid="lr-leads-panel">
          <div className="flex items-center gap-2 mb-4">
            <select
              data-testid="lr-leads-status-filter"
              value={leadsStatus}
              onChange={e => { setLeadsStatus(e.target.value); loadLeads(e.target.value); }}
              className="text-sm border border-slate-200 rounded-lg px-3 py-1.5 bg-white text-slate-700"
            >
              <option value="">All States</option>
              <option value="inactive">Inactive</option>
              <option value="scheduled">Scheduled</option>
              <option value="in_sequence">In Sequence</option>
              <option value="paused">Paused</option>
              <option value="suppressed">Suppressed</option>
              <option value="stopped">Stopped</option>
              <option value="no_qualifying_comm">Needs First Contact</option>
            </select>
            <span className="text-sm text-slate-500">{leadsTotal} lead(s)</span>
          </div>

          <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 border-b border-slate-100">
                <tr>
                  {["Name", "Email", "Brand", "State", "Last Sent", "Next Due", "Actions"].map(h => (
                    <th key={h} className="px-4 py-2.5 text-left text-xs font-semibold text-slate-500 uppercase tracking-wide">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-50">
                {leads.length === 0 && (
                  <tr><td colSpan={7} className="px-4 py-8 text-center text-slate-400 text-sm">No leads found</td></tr>
                )}
                {leads.map(lead => {
                  const rs = lead.recovery_state || {};
                  return (
                    <tr key={lead.id} className="hover:bg-slate-50 transition" data-testid={`lr-lead-row-${lead.id}`}>
                      <td className="px-4 py-3 font-medium text-slate-800">{lead.full_name || "—"}</td>
                      <td className="px-4 py-3 text-slate-500 text-xs">{lead.email || "—"}</td>
                      <td className="px-4 py-3 text-xs text-slate-500">{lead.brand || "—"}</td>
                      <td className="px-4 py-3"><StateBadge status={rs.status || "inactive"} /></td>
                      <td className="px-4 py-3 text-xs text-slate-500">
                        {rs.last_sent_at ? `D${rs.last_sent_day} · ${new Date(rs.last_sent_at).toLocaleDateString()}` : "—"}
                      </td>
                      <td className="px-4 py-3 text-xs text-slate-500">
                        {rs.next_due_at ? new Date(rs.next_due_at).toLocaleDateString() : "—"}
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-1.5">
                          {rs.status === "paused" && (
                            <button
                              data-testid={`lr-resume-${lead.id}`}
                              onClick={() => leadAction(lead.id, "resume")}
                              className="p-1 rounded hover:bg-emerald-50 text-emerald-600 transition"
                              title="Resume sequence"
                            >
                              <Play size={13} />
                            </button>
                          )}
                          {["in_sequence", "scheduled", "inactive"].includes(rs.status || "inactive") && (
                            <button
                              data-testid={`lr-pause-${lead.id}`}
                              onClick={() => leadAction(lead.id, "pause", "manual_staff_pause")}
                              className="p-1 rounded hover:bg-amber-50 text-amber-600 transition"
                              title="Pause sequence"
                            >
                              <Pause size={13} />
                            </button>
                          )}
                          {rs.status !== "suppressed" && (
                            <button
                              data-testid={`lr-suppress-${lead.id}`}
                              onClick={() => {
                                if (window.confirm("Suppress this lead from all future recovery emails?"))
                                  leadAction(lead.id, "suppress", "staff_removed");
                              }}
                              className="p-1 rounded hover:bg-rose-50 text-rose-500 transition"
                              title="Suppress"
                            >
                              <Ban size={13} />
                            </button>
                          )}
                          {lead.has_suggested_reply && (
                            <span className="text-xs bg-purple-100 text-purple-700 px-1.5 py-0.5 rounded-full" title="CHASE has a suggested reply draft">
                              Draft
                            </span>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* ── Message Log ── */}
      {tab === "messages" && (
        <div data-testid="lr-messages-panel">
          <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 border-b border-slate-100">
                <tr>
                  {["Lead ID", "Day", "Status", "Template", "Sent At", "Personalization"].map(h => (
                    <th key={h} className="px-4 py-2.5 text-left text-xs font-semibold text-slate-500 uppercase tracking-wide">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-50">
                {messages.length === 0 && (
                  <tr><td colSpan={6} className="px-4 py-8 text-center text-slate-400">No messages yet</td></tr>
                )}
                {messages.map(msg => (
                  <tr key={msg.id || msg.claim_id} className="hover:bg-slate-50" data-testid={`lr-msg-${msg.claim_id}`}>
                    <td className="px-4 py-3 font-mono text-xs text-slate-500">{(msg.client_id || "").slice(0, 8)}…</td>
                    <td className="px-4 py-3 font-medium">D{msg.day_key}</td>
                    <td className="px-4 py-3">
                      <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${
                        msg.status === "sent" ? "bg-emerald-100 text-emerald-700"
                          : msg.status === "claimed" ? "bg-sky-100 text-sky-700"
                          : msg.status === "failed" ? "bg-rose-100 text-rose-700"
                          : msg.status === "bounced" ? "bg-orange-100 text-orange-700"
                          : "bg-slate-100 text-slate-500"
                      }`}>
                        {msg.status}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-xs text-slate-500">{msg.template_key || "—"}</td>
                    <td className="px-4 py-3 text-xs text-slate-500">
                      {msg.sent_at ? new Date(msg.sent_at).toLocaleString() : "—"}
                    </td>
                    <td className="px-4 py-3 text-xs text-slate-500">
                      {msg.personalization_source
                        ? `${msg.personalization_source} (${((msg.personalization_confidence || 0) * 100).toFixed(0)}%)`
                        : "—"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* ── Templates ── */}
      {tab === "templates" && (
        <div data-testid="lr-templates-panel" className="space-y-4">
          {templates.map(t => (
            <TemplateCard key={t.key} template={t} onSave={async (key, updates) => {
              try {
                await ax("put", `/api/lead-recovery/templates/${key}`, updates);
                toast.success("Template saved");
                await loadTemplates();
              } catch (e) { toast.error("Save failed: " + (e.response?.data?.detail || e.message)); }
            }} />
          ))}
          {templates.length === 0 && (
            <div className="text-center py-12 text-slate-400">No templates found. They will be seeded on first dispatch.</div>
          )}
        </div>
      )}

      {/* ── Config ── */}
      {tab === "config" && config && (
        <div data-testid="lr-config-panel" className="max-w-xl space-y-5">
          <div className="bg-amber-50 border border-amber-200 rounded-lg px-4 py-3 text-sm text-amber-800 flex items-start gap-2">
            <AlertTriangle size={15} className="mt-0.5 shrink-0" />
            <span>
              <strong>Production pilot not yet authorized.</strong> These settings configure the engine for when Management approves the Production pilot. No live marketing emails will be sent until then.
            </span>
          </div>

          <ConfigRow label="Engine Enabled" description="Master switch — only turn on after Production pilot authorization">
            <button
              data-testid="lr-config-toggle-engine"
              onClick={toggleEngine}
              className={`px-4 py-1.5 rounded-lg text-sm font-medium transition border ${
                config.enabled
                  ? "bg-rose-50 border-rose-200 text-rose-700 hover:bg-rose-100"
                  : "bg-emerald-50 border-emerald-200 text-emerald-700 hover:bg-emerald-100"
              }`}
            >
              {config.enabled ? "Disable" : "Enable"}
            </button>
          </ConfigRow>

          <ConfigRow label="Daily Cap" description="Maximum recovery emails sent per day">
            <input
              data-testid="lr-config-daily-cap"
              type="number"
              defaultValue={config.daily_cap}
              min={1} max={200}
              onBlur={e => updateConfig("daily_cap", parseInt(e.target.value))}
              className="w-20 border border-slate-200 rounded-lg px-3 py-1.5 text-sm text-center"
            />
          </ConfigRow>

          <ConfigRow label="Sending Window (UTC)" description="Only send within this hour range (UTC)">
            <div className="flex items-center gap-2 text-sm">
              <input
                data-testid="lr-config-window-start"
                type="number"
                defaultValue={config.sending_window_start_utc}
                min={0} max={23}
                onBlur={e => updateConfig("sending_window_start_utc", parseInt(e.target.value))}
                className="w-16 border border-slate-200 rounded-lg px-2 py-1.5 text-center"
              />
              <span className="text-slate-400">to</span>
              <input
                data-testid="lr-config-window-end"
                type="number"
                defaultValue={config.sending_window_end_utc}
                min={0} max={23}
                onBlur={e => updateConfig("sending_window_end_utc", parseInt(e.target.value))}
                className="w-16 border border-slate-200 rounded-lg px-2 py-1.5 text-center"
              />
            </div>
          </ConfigRow>

          <ConfigRow label="CHASE Personalization" description="Allow CHASE to personalize recovery message copy">
            <ToggleBtn
              data-testid="lr-config-personalization"
              value={config.chase_personalization_enabled}
              onChange={v => updateConfig("chase_personalization_enabled", v)}
            />
          </ConfigRow>

          <ConfigRow label="Personalization Confidence Threshold" description="Min CHASE confidence score to use personalized copy (0–1)">
            <input
              data-testid="lr-config-confidence"
              type="number"
              defaultValue={config.personalization_confidence_threshold}
              min={0} max={1} step={0.05}
              onBlur={e => updateConfig("personalization_confidence_threshold", parseFloat(e.target.value))}
              className="w-20 border border-slate-200 rounded-lg px-3 py-1.5 text-sm text-center"
            />
          </ConfigRow>

          <ConfigRow label="IMAP Health Check Bypass" description="Preview only — bypasses CHASE IMAP health gate">
            <ToggleBtn
              data-testid="lr-config-imap-bypass"
              value={config.ignore_imap_health_check}
              onChange={v => updateConfig("ignore_imap_health_check", v)}
              warning="Preview only"
            />
          </ConfigRow>

          <ConfigRow label="Unsubscribe Base URL" description="Your app's public base URL for one-click unsubscribe links">
            <input
              data-testid="lr-config-unsub-url"
              type="text"
              defaultValue={config.unsubscribe_base_url || ""}
              placeholder="https://app.expattaxfirm.com"
              onBlur={e => updateConfig("unsubscribe_base_url", e.target.value)}
              className="w-72 border border-slate-200 rounded-lg px-3 py-1.5 text-sm"
            />
          </ConfigRow>
        </div>
      )}

      {/* ── Simulate ── */}
      {tab === "simulate" && (
        <div data-testid="lr-simulate-panel" className="max-w-lg space-y-4">
          <div className="bg-sky-50 border border-sky-200 rounded-lg px-4 py-3 text-sm text-sky-800 flex items-start gap-2">
            <Eye size={14} className="mt-0.5 shrink-0" />
            <span>Simulation is a dry-run — no emails are sent. Use a lead ID from the Leads tab.</span>
          </div>

          <div className="bg-white border border-slate-200 rounded-xl p-4 shadow-sm">
            <label className="block text-sm font-medium text-slate-700 mb-2">Lead ID</label>
            <div className="flex gap-2">
              <input
                data-testid="lr-sim-id-input"
                value={simId}
                onChange={e => setSimId(e.target.value)}
                placeholder="Enter a lead client_id…"
                className="flex-1 border border-slate-200 rounded-lg px-3 py-2 text-sm"
              />
              <button
                data-testid="lr-sim-run-btn"
                onClick={simulate}
                disabled={simRunning || !simId.trim()}
                className="px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white text-sm font-medium rounded-lg transition disabled:opacity-50"
              >
                {simRunning ? "Running…" : "Simulate"}
              </button>
            </div>
          </div>

          {simResult && (
            <div className="bg-white border border-slate-200 rounded-xl p-4 shadow-sm" data-testid="lr-sim-result">
              <h3 className="text-sm font-semibold text-slate-700 mb-3">Simulation Result</h3>
              {simResult.error ? (
                <p className="text-rose-600 text-sm">{simResult.error}</p>
              ) : (
                <dl className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <dt className="text-slate-500">Outcome</dt>
                    <dd className="font-semibold">{simResult.outcome}</dd>
                  </div>
                  {simResult.eligibility && (
                    <div className="flex justify-between">
                      <dt className="text-slate-500">Eligibility</dt>
                      <dd>{simResult.eligibility.eligible ? "✓ Eligible" : `✗ ${simResult.eligibility.reason}`}</dd>
                    </div>
                  )}
                  {simResult.day_key && (
                    <div className="flex justify-between">
                      <dt className="text-slate-500">Would-send Day</dt>
                      <dd>Day {simResult.day_key}</dd>
                    </div>
                  )}
                  {simResult.subject && (
                    <div>
                      <dt className="text-slate-500 mb-1">Subject</dt>
                      <dd className="text-slate-800 bg-slate-50 p-2 rounded text-xs">{simResult.subject}</dd>
                    </div>
                  )}
                  {simResult.personalization_source && (
                    <div className="flex justify-between">
                      <dt className="text-slate-500">Personalization</dt>
                      <dd>{simResult.personalization_source} ({((simResult.personalization_confidence || 0) * 100).toFixed(0)}%)</dd>
                    </div>
                  )}
                </dl>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ── Template editor card ───────────────────────────────────────────────────────
function TemplateCard({ template: t, onSave }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState({ ...t });

  return (
    <div className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden" data-testid={`lr-template-${t.key}`}>
      <div className="flex items-center justify-between px-4 py-3 border-b border-slate-100 bg-slate-50">
        <div>
          <span className="text-xs font-semibold text-purple-700 bg-purple-50 px-2 py-0.5 rounded-full">D{t.day_key}</span>
          <span className="ml-2 text-sm font-medium text-slate-700">{t.label}</span>
        </div>
        <div className="flex items-center gap-2">
          <span className={`text-xs px-2 py-0.5 rounded-full ${t.is_active ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-500"}`}>
            {t.is_active ? "Active" : "Inactive"}
          </span>
          <button
            data-testid={`lr-template-edit-${t.key}`}
            onClick={() => setEditing(!editing)}
            className="text-xs text-purple-600 hover:text-purple-800 font-medium"
          >
            {editing ? "Cancel" : "Edit"}
          </button>
        </div>
      </div>

      {!editing ? (
        <div className="p-4 space-y-2 text-sm">
          <div><span className="text-slate-500 text-xs">Subject:</span> <span className="font-medium">{t.subject}</span></div>
          <div><span className="text-slate-500 text-xs">Heading:</span> <span>{t.heading}</span></div>
          <div><span className="text-slate-500 text-xs">Body:</span> <span className="text-slate-600">{(t.intro || "").slice(0, 120)}…</span></div>
        </div>
      ) : (
        <div className="p-4 space-y-3">
          <Field label="Subject">
            <input
              value={draft.subject}
              onChange={e => setDraft({ ...draft, subject: e.target.value })}
              className="w-full border border-slate-200 rounded-lg px-3 py-1.5 text-sm"
              data-testid={`lr-template-subject-${t.key}`}
            />
          </Field>
          <Field label="Heading">
            <input
              value={draft.heading}
              onChange={e => setDraft({ ...draft, heading: e.target.value })}
              className="w-full border border-slate-200 rounded-lg px-3 py-1.5 text-sm"
              data-testid={`lr-template-heading-${t.key}`}
            />
          </Field>
          <Field label="Body (intro)">
            <textarea
              value={draft.intro}
              onChange={e => setDraft({ ...draft, intro: e.target.value })}
              rows={4}
              className="w-full border border-slate-200 rounded-lg px-3 py-1.5 text-sm"
              data-testid={`lr-template-intro-${t.key}`}
            />
          </Field>
          <Field label="CTA Label">
            <input
              value={draft.cta_label}
              onChange={e => setDraft({ ...draft, cta_label: e.target.value })}
              className="w-full border border-slate-200 rounded-lg px-3 py-1.5 text-sm"
            />
          </Field>
          <div className="flex justify-end gap-2 mt-2">
            <button onClick={() => setEditing(false)} className="px-3 py-1.5 text-sm text-slate-600 border border-slate-200 rounded-lg hover:bg-slate-50">Cancel</button>
            <button
              data-testid={`lr-template-save-${t.key}`}
              onClick={async () => { await onSave(t.key, draft); setEditing(false); }}
              className="px-4 py-1.5 text-sm bg-purple-600 text-white rounded-lg hover:bg-purple-700"
            >
              Save
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Helper subcomponents ───────────────────────────────────────────────────────
function ConfigRow({ label, description, children }) {
  return (
    <div className="bg-white border border-slate-200 rounded-xl px-4 py-3 shadow-sm flex items-start justify-between gap-4">
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium text-slate-700">{label}</p>
        {description && <p className="text-xs text-slate-400 mt-0.5">{description}</p>}
      </div>
      <div className="shrink-0">{children}</div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label className="block text-xs font-medium text-slate-500 mb-1">{label}</label>
      {children}
    </div>
  );
}

function ToggleBtn({ value, onChange, warning, "data-testid": testid }) {
  return (
    <div className="flex items-center gap-2">
      {warning && <span className="text-xs text-amber-600">{warning}</span>}
      <button
        data-testid={testid}
        onClick={() => onChange(!value)}
        className={`relative w-9 h-5 rounded-full transition-colors ${value ? "bg-purple-600" : "bg-slate-300"}`}
      >
        <span className={`absolute top-0.5 left-0.5 w-4 h-4 rounded-full bg-white shadow transition-transform ${value ? "translate-x-4" : ""}`} />
      </button>
    </div>
  );
}
